import { cn } from '@/lib/utils';
import { LEAD_STAGES, type StageHistoryEntry } from './types';
import { Check } from 'lucide-react';

export interface PipelineStagesProps {
  currentStage: string;
  stageHistory?: StageHistoryEntry[];
  onStageClick?: (stage: string) => void;
}

/**
 * PipelineStages - Horizontal/Vertical stepper showing 10 lead stages
 *
 * Features:
 * - Current stage: Blue circle with bold text
 * - Past stages: Green checkmark, solid line
 * - Future stages: Gray circle, dashed line
 * - Responsive: Horizontal on desktop, vertical on mobile
 * - Optional click handler for stage navigation
 * - Shows stage history with timestamps on mobile
 */
export function PipelineStages({
  currentStage,
  stageHistory = [],
  onStageClick,
}: PipelineStagesProps) {
  const currentIndex = LEAD_STAGES.findIndex((s) => s.id === currentStage);

  // Get history stages as a set for quick lookup
  const historyStages = new Set(stageHistory.map((h) => h.stage));

  return (
    <div className="w-full py-6 overflow-x-auto">
      {/* Desktop: Horizontal stepper */}
      <div className="hidden md:flex items-center justify-between min-w-max px-4">
        {LEAD_STAGES.map((stage, index) => {
          const isCurrent = stage.id === currentStage;
          const isPast = index < currentIndex || historyStages.has(stage.id);
          const isFuture = index > currentIndex && !historyStages.has(stage.id);
          const isClickable = onStageClick !== undefined;

          return (
            <div key={stage.id} className="flex items-center flex-1">
              {/* Stage circle and label */}
              <button
                onClick={() => isClickable && onStageClick(stage.id)}
                disabled={!isClickable}
                className={cn(
                  'flex flex-col items-center gap-2 transition-all min-h-[44px]',
                  isClickable && 'cursor-pointer hover:opacity-80',
                  !isClickable && 'cursor-default'
                )}
              >
                {/* Circle */}
                <div
                  className={cn(
                    'flex items-center justify-center rounded-full transition-all',
                    'w-10 h-10 border-2',
                    // Current stage: Blue with bold border
                    isCurrent && 'bg-blue-600 border-blue-600 text-white',
                    // Past stage: Green with checkmark
                    isPast && !isCurrent && 'bg-green-600 border-green-600 text-white',
                    // Future stage: Gray outline
                    isFuture && 'bg-white border-gray-300 text-gray-400'
                  )}
                >
                  {isPast && !isCurrent ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>

                {/* Label */}
                <span
                  className={cn(
                    'text-xs text-center transition-all max-w-[100px]',
                    isCurrent && 'font-bold text-blue-600',
                    isPast && !isCurrent && 'font-medium text-green-600',
                    isFuture && 'text-gray-400'
                  )}
                >
                  {stage.label}
                </span>
              </button>

              {/* Connecting line (except after last stage) */}
              {index < LEAD_STAGES.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 min-w-[40px]">
                  <div
                    className={cn(
                      'h-full transition-all',
                      // Past connection: Solid green
                      isPast && 'bg-green-600',
                      // Future connection: Dashed gray
                      isFuture && 'border-t-2 border-dashed border-gray-300'
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: Vertical stepper */}
      <div className="md:hidden space-y-4 px-4">
        {LEAD_STAGES.map((stage, index) => {
          const isCurrent = stage.id === currentStage;
          const isPast = index < currentIndex || historyStages.has(stage.id);
          const isFuture = index > currentIndex && !historyStages.has(stage.id);
          const isClickable = onStageClick !== undefined;

          return (
            <div key={stage.id} className="flex items-start gap-3">
              {/* Circle and vertical line container */}
              <div className="flex flex-col items-center">
                {/* Circle */}
                <button
                  onClick={() => isClickable && onStageClick(stage.id)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center justify-center rounded-full transition-all min-h-[44px] min-w-[44px]',
                    'w-10 h-10 border-2',
                    isClickable && 'cursor-pointer hover:opacity-80',
                    !isClickable && 'cursor-default',
                    // Current stage: Blue
                    isCurrent && 'bg-blue-600 border-blue-600 text-white',
                    // Past stage: Green with checkmark
                    isPast && !isCurrent && 'bg-green-600 border-green-600 text-white',
                    // Future stage: Gray outline
                    isFuture && 'bg-white border-gray-300 text-gray-400'
                  )}
                >
                  {isPast && !isCurrent ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </button>

                {/* Vertical connecting line (except after last stage) */}
                {index < LEAD_STAGES.length - 1 && (
                  <div className="w-0.5 h-12 my-1">
                    <div
                      className={cn(
                        'h-full transition-all',
                        // Past connection: Solid green
                        isPast && 'bg-green-600',
                        // Future connection: Dashed gray
                        isFuture && 'border-l-2 border-dashed border-gray-300'
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Label and history info */}
              <div className="flex-1 pt-2">
                <span
                  className={cn(
                    'text-sm transition-all',
                    isCurrent && 'font-bold text-blue-600',
                    isPast && !isCurrent && 'font-medium text-green-600',
                    isFuture && 'text-gray-400'
                  )}
                >
                  {stage.label}
                </span>

                {/* Show timestamp if in history */}
                {stageHistory.length > 0 && historyStages.has(stage.id) && (
                  <div className="mt-1 text-xs text-gray-500">
                    {(() => {
                      const entry = stageHistory.find((h) => h.stage === stage.id);
                      if (!entry) return null;
                      const date = new Date(entry.timestamp);
                      return (
                        <div>
                          <div>{date.toLocaleDateString('is-IS')}</div>
                          <div className="text-gray-400">{entry.agent}</div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
